// Central shortcut registry — single source of truth for keybindings AND display
// hints used in tooltips, command palette items, dropdown menu items, and the
// shortcuts help panel.

export type ShortcutGroup =
  | "navigation"
  | "actions"
  | "global"
  | "search"
  | "help"
  | "detail-nav";

export interface ShortcutDef {
  /** Stable identifier for this shortcut. */
  id: string;
  /** tinykeys binding string (e.g. "g h", "$mod+k", "?"). */
  keys: string;
  /** Human-readable display (e.g. "G then H", "⌘K", "?"). */
  display: string;
  /** Compact display for tooltips (e.g. "G H", "⌘K"). */
  compact: string;
  /** Action description used in palette and help panel. */
  label: string;
  /** Grouping bucket for the help panel and command palette. */
  group: ShortcutGroup;
  /** Optional href for nav shortcuts — palette uses this directly. */
  href?: string;
  /** Aliases used by cmdk fuzzy filter (e.g. ["home", "back"]). */
  keywords?: string[];
}

// Detect mac at module-load. Tests render in happy-dom; navigator may exist.
function detectMac(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(
    navigator.platform || navigator.userAgent,
  );
}

const IS_MAC = detectMac();

/** Returns true on Mac. Exported for tests + display helpers. */
export const isMac = (): boolean => IS_MAC;

/** Compact ⌘ on Mac, "Ctrl" elsewhere. */
export const modSymbol = (): string => (IS_MAC ? "⌘" : "Ctrl");

/**
 * Build the full SHORTCUTS list. We compute mod-dependent display strings at
 * module load — IS_MAC is stable for the lifetime of the page.
 */
export const SHORTCUTS: readonly ShortcutDef[] = [
  // Navigation — vim-style "go" sequences
  {
    id: "goHome",
    keys: "g h",
    display: "G then H",
    compact: "G H",
    label: "Go to home",
    group: "navigation",
    href: "/dashboard",
    keywords: ["home", "dashboard"],
  },
  {
    id: "goCustomers",
    keys: "g c",
    display: "G then C",
    compact: "G C",
    label: "Go to customers",
    group: "navigation",
    href: "/customers",
    keywords: ["customers", "contacts", "directory"],
  },
  {
    id: "goContacts",
    keys: "g k",
    display: "G then K",
    compact: "G K",
    label: "Go to contacts",
    group: "navigation",
    href: "/contacts",
    keywords: ["contacts", "people", "directory", "roles"],
  },
  {
    id: "goOpportunities",
    keys: "g o",
    display: "G then O",
    compact: "G O",
    label: "Go to opportunities",
    group: "navigation",
    href: "/opportunities",
    keywords: ["opportunities", "pipeline"],
  },
  {
    id: "goJobs",
    keys: "g j",
    display: "G then J",
    compact: "G J",
    label: "Go to jobs",
    group: "navigation",
    href: "/jobs",
    keywords: ["jobs", "work orders", "operations"],
  },
  {
    id: "goInvoices",
    keys: "g i",
    display: "G then I",
    compact: "G I",
    label: "Go to invoices",
    group: "navigation",
    href: "/invoices",
    keywords: ["invoices", "billing", "finance"],
  },
  {
    id: "goSettings",
    keys: "g s",
    display: "G then S",
    compact: "G S",
    label: "Go to settings",
    group: "navigation",
    href: "/settings",
    keywords: ["settings", "preferences", "config"],
  },
  // Global controls
  {
    id: "openCommand",
    keys: "$mod+k",
    display: `${modSymbol()}K`,
    compact: `${modSymbol()}K`,
    label: "Open command palette",
    group: "global",
    keywords: ["command", "palette", "search", "menu"],
  },
  {
    id: "openSearch",
    keys: "/",
    display: "/",
    compact: "/",
    label: "Search",
    group: "search",
    keywords: ["search", "find"],
  },
  {
    id: "openShortcutsHelp",
    keys: "Shift+?",
    display: "?",
    compact: "?",
    label: "Keyboard shortcuts",
    group: "help",
    keywords: ["shortcuts", "help", "keyboard", "keys"],
  },
  // Detail-page record navigation — bound locally inside <DetailPageNav>
  // when mounted (no `href`, so useNavigationShortcuts will not auto-bind).
  // Documented in the registry so the help dialog lists them.
  {
    id: "navPrevRecord",
    keys: "k",
    display: "K",
    compact: "K",
    label: "Previous record",
    group: "detail-nav",
    keywords: ["previous", "prev", "back", "record"],
  },
  {
    id: "navNextRecord",
    keys: "j",
    display: "J",
    compact: "J",
    label: "Next record",
    group: "detail-nav",
    keywords: ["next", "forward", "record"],
  },
] as const;

const BY_ID = new Map<string, ShortcutDef>(SHORTCUTS.map((s) => [s.id, s]));

/** Look up a shortcut by id. Returns undefined if not found. */
export function getShortcut(id: string): ShortcutDef | undefined {
  return BY_ID.get(id);
}

/** Filter shortcuts by group. Stable order matches registry order. */
export function shortcutsByGroup(group: ShortcutGroup): ShortcutDef[] {
  return SHORTCUTS.filter((s) => s.group === group);
}

/**
 * Returns true when the user is typing into a text input, textarea, or any
 * contentEditable element. Use this to suppress single-key shortcuts that
 * would otherwise eat keystrokes during typing. ⌘K and Escape are always
 * handled even when input is focused.
 */
export function isInputFocused(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false;
  const el = target as HTMLElement;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}
